/** VISTOERA_VIDEO_SHOT_REFERENCE_ASSETS_KEYFRAME_INPUT_AND_INHERITED_FORMAT_LOCK_P0_V1
 *
 * Shot-level structured reference assets + Storyboard production-format
 * inheritance helpers for `generate_video`. Keeps image identity on real
 * `assetId`s (never labels alone) so keyframe generation can wire
 * multimodal reference edges into `generate_image`.
 */

import type { InputAssetCategory, InputAssetRef } from './videoKeyframeTimeline'

export interface SupportingAssetLike {
  asset_id?: string | null
  display_name?: string | null
  asset_type?: string | null
}

export interface VideoFormatInheritance {
  formatLocked: boolean
  ratio: string
  qualityTier: string
  inheritedFormatLabel: string | null
}

const CHARACTER_ASSET_TYPES = new Set(['character', 'virtual_ip', 'vip', 'person', 'model', '模特', '人物'])
const PRODUCT_ASSET_TYPES = new Set(['product', 'object', 'prop', '产品', '道具', '物品'])
const SCENE_ASSET_TYPES = new Set(['scene', 'location', 'background', '场景', '环境'])

function normalizeName(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
}

function namesMatch(a: string, b: string): boolean {
  if (!a || !b) return false
  return a === b || a.includes(b) || b.includes(a)
}

function categoryMatchesAssetType(category: InputAssetCategory, assetType: string | null | undefined): boolean {
  const t = normalizeName(assetType)
  if (!t || t === 'unknown') return true
  if (category === 'character') return CHARACTER_ASSET_TYPES.has(t) || /char|person|model|vip|模特|人物/.test(t)
  if (category === 'object') return PRODUCT_ASSET_TYPES.has(t) || /product|object|prop|产品|道具/.test(t)
  if (category === 'scene') return SCENE_ASSET_TYPES.has(t) || /scene|location|场景/.test(t)
  return true
}

/** Fill missing `assetId` on shot refs by matching Storyboard/Script
 * supporting assets (display_name ↔ character/product/scene label). */
export function enrichInputAssetRefsFromSupportingAssets(
  refs: InputAssetRef[],
  supportingAssets: SupportingAssetLike[] | null | undefined,
): InputAssetRef[] {
  const pool = (supportingAssets || []).filter((a) => a?.asset_id)
  if (!pool.length) return refs

  const used = new Set<string>()
  return refs.map((ref) => {
    if (ref.assetId) return ref
    const label = normalizeName(ref.label)
    const entity = normalizeName(ref.entityRefId)
    const match = pool.find((a) => {
      if (!a.asset_id || used.has(a.asset_id)) return false
      if (!categoryMatchesAssetType(ref.category, a.asset_type)) return false
      const name = normalizeName(a.display_name)
      return namesMatch(name, label) || (entity ? namesMatch(name, entity) : false)
    })
    if (!match?.asset_id) return ref
    used.add(match.asset_id)
    return { ...ref, assetId: match.asset_id }
  })
}

/** Stable fingerprint of structured refs (ids + categories + labels).
 * Used to mark Current Result stale when the user swaps references. */
export function referenceAssetsFingerprint(refs: InputAssetRef[]): string {
  const rows = [...refs]
    .map((r) => `${r.category}:${r.entityRefId || ''}:${r.label}:${r.assetId || ''}`)
    .sort()
  return JSON.stringify(rows)
}

export function refsWithImageAssets(refs: InputAssetRef[]): InputAssetRef[] {
  return refs.filter((r) => Boolean(r.assetId))
}

/** Prefer character → product/object → scene → other when wiring a
 * limited number of reference edges into keyframe image generation. */
export function prioritizeRefsForKeyframe(refs: InputAssetRef[], max = 4): InputAssetRef[] {
  const order: Record<InputAssetCategory, number> = {
    character: 0,
    object: 1,
    scene: 2,
    reference: 3,
  }
  return [...refsWithImageAssets(refs)]
    .sort((a, b) => order[a.category] - order[b.category] || a.label.localeCompare(b.label))
    .slice(0, Math.max(0, max))
}

export function buildKeyframeImagePrompt(args: {
  visualDescription?: string | null
  motionIntent?: string | null
  cameraIntent?: string | null
  refs: InputAssetRef[]
}): string {
  const bits = [
    args.visualDescription?.trim() || null,
    args.motionIntent?.trim() ? `动作：${args.motionIntent.trim()}` : null,
    args.cameraIntent?.trim() ? `镜头：${args.cameraIntent.trim()}` : null,
  ].filter(Boolean) as string[]
  const withAssets = prioritizeRefsForKeyframe(args.refs)
  if (withAssets.length) {
    bits.push(
      `视觉参考已作为输入图片接入（${withAssets.map((r) => `${r.category}:${r.label}`).join('、')}）；保持人物/产品身份与服装一致。`,
    )
  } else if (args.refs.length) {
    bits.push(`涉及素材（仅名称，无图片）：${args.refs.map((r) => r.label).join('、')}`)
  }
  return bits.join('。') || '生成视频关键帧'
}

export function resolveStoryboardFormatInheritance(args: {
  aspectRatio?: string | null
  qualityTier?: string | null
  storyboardTitle?: string | null
  fallbackQualityTier?: string
  productionFormat?: { aspect_ratio?: string; resolution?: string; version?: number } | null
}): VideoFormatInheritance {
  const fromContractAspect = args.productionFormat?.aspect_ratio
  const fromContractRes = args.productionFormat?.resolution
  const ratio =
    (fromContractAspect && fromContractAspect !== 'AUTO' ? fromContractAspect : null) ||
    (args.aspectRatio && args.aspectRatio !== 'AUTO' ? args.aspectRatio : null) ||
    '16:9'
  const qualityFromRes =
    fromContractRes && /1080/i.test(String(fromContractRes))
      ? '1080P'
      : fromContractRes && /720/i.test(String(fromContractRes))
        ? '720P'
        : null
  const qualityTier = qualityFromRes || args.qualityTier || args.fallbackQualityTier || '1080P'
  return {
    formatLocked: true,
    ratio,
    qualityTier,
    inheritedFormatLabel: args.storyboardTitle?.trim() || null,
  }
}

export function isVideoFormatLocked(fields: Record<string, unknown>): boolean {
  return (
    Boolean(fields.formatLocked) ||
    Boolean(fields.lockedProductionFormat) ||
    Boolean(fields.boundShotId) ||
    Boolean(fields.boundStoryboardArtifactId)
  )
}
